import { Component, ElementRef, EventEmitter, forwardRef, Input, NgModule, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { map, startWith, switchMap, takeUntil, tap, filter, catchError, BehaviorSubject, combineLatest, fromEvent, of } from 'rxjs';
import { CommonModule, AsyncPipe } from '@angular/common';
import { BaseControl } from '@uw/core/base-control';
import { TranslateModule } from '@ngx-translate/core';
import { MetadataService } from '@uw/core/services/metadata.service';
import { guid } from '@uw/core/utils/util-functions';
import { DEFAULT_OPTION } from '@uw/core/constants';
import { MatDatepickerModule, MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule, MatSuffix } from '@angular/material/input';

@Component({
    selector: 'uw-filter-detail-editor',
    templateUrl: './filter-detail-editor.component.html',
    styleUrls: ['./filter-detail-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FilterDetailEditorComponent),
            multi: true
        }
    ],
    imports: [ReactiveFormsModule, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, AsyncPipe, TranslateModule]
})
export class FilterDetailEditorComponent extends BaseControl {

  readonly DEFAULT_OPTION = DEFAULT_OPTION;

  readonly form = new UntypedFormGroup({
    id: new UntypedFormControl(guid(), []),
    criteria: new UntypedFormControl(DEFAULT_OPTION, [Validators.required]),
    condition: new UntypedFormControl(DEFAULT_OPTION, [Validators.required]),
    selection: new UntypedFormControl(DEFAULT_OPTION, [Validators.required]),
  });

  get criteriaControl(): UntypedFormControl {
    return this.form.get('criteria') as UntypedFormControl;
  }

  private readonly _campaignIdSubject: BehaviorSubject<any> = new BehaviorSubject(null);

  @Input("campaignId") set campaignId(value: any) {
    this._campaignIdSubject.next(value);
  }

  @Input("isEmail") set isEmail(value: any) {
    this._isEmailSubject.next(value);
  }

  private readonly _isEmailSubject: BehaviorSubject<any> = new BehaviorSubject(false);

  @Input("isEmailSubject") set isEmailSubject(value: any) {
    this.isEmailSubject.next(true);
  }

  readonly vm$ = combineLatest([this._campaignIdSubject, this._isEmailSubject]).pipe(
    switchMap(([campaignId, isEmail]) => combineLatest([
      this._metadataService.getFilterCritera(campaignId, isEmail),
      this.criteriaControl.valueChanges.pipe(
        switchMap(criteria => criteria && criteria != DEFAULT_OPTION ? this._metadataService.getFilterConditions(criteria) : of([])),
        startWith([])
      ),
      this.criteriaControl.valueChanges.pipe(
        filter(criteria => criteria && criteria != DEFAULT_OPTION),
        switchMap(criteria => criteria && criteria != DEFAULT_OPTION ? this._metadataService.getFilterSelections(criteria, campaignId).pipe(
          catchError(e => of([])),
        ) : of([])),
        startWith([])
      ),
      this.criteriaControl.valueChanges.pipe(
        filter(criteria => criteria && criteria != DEFAULT_OPTION),
        tap(criteria => {
          this.form.patchValue({
            selection: this.selectable(criteria) ? DEFAULT_OPTION : null
          })
        }),
        startWith(null)
      )
    ])
      .pipe(
        map(([criteria, conditions, selections]) => {
          return {
            criteria,
            conditions,
            selections
          };
        })
      ))
  );

  selectable(criteria: string) {
    const lookup = ["Language", "TargetType", "NotReadyOption", "DRC"];
    return lookup.indexOf(criteria) > -1;
  }

  constructor(
    private readonly _elementRef: ElementRef,
    private readonly _metadataService: MetadataService
  ) {
    super();
  }

  @Output() readonly create: EventEmitter<any> = new EventEmitter();

  handleCreateClick() {
    this.create.emit(this.form.value);
    this.writeValue(null);
  }

  public onAddKeydown(event: any): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleCreateClick();
    }
  }

  writeValue(obj: any): void {
    if (obj == null) {
      this.form.reset({
        id: guid(),
        criteria: DEFAULT_OPTION,
        condition: DEFAULT_OPTION,
        selection: DEFAULT_OPTION
      });
    }
    else {
      this.form.patchValue(obj, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.form.valueChanges
      .pipe(takeUntil(this._destroyed$))
      .subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this._elementRef.nativeElement
      .querySelectorAll("*")
      .forEach((element: HTMLElement) => {
        fromEvent(element, "focus")
          .pipe(
            takeUntil(this._destroyed$),
            tap(x => fn())
          )
          .subscribe();
      });
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }
}

@NgModule({
    exports: [
        FilterDetailEditorComponent
    ],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatInputModule,
        FilterDetailEditorComponent
    ]
})
export class FilterDetailEditorModule { }
